import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getImageNotificationDetail,
  updateImageNotification,
  clearErrors,
} from "../../../actions/adminImageNotificationActions";
import { UPDATE_IMAGE_NOTIFICATION_RESET } from "../../../constants/adminImageNotificationConstants";
import Loader from "../../Loader";
import { imageNotificationUrl } from "../../../utils/assets";

const EditImageNotification = ({ history, match }) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const notificationId = match.params.id;

  const [formData, setFormData] = useState({
    title: "",
    image: "",
    imageName: "",
    oldImage: "",
    link: "",
    message: "",
    backgroundColor: "#16a34a",
    messageSpeed: 5,
    displayLocation: "landing",
    active: true,
    expiresAt: "",
    countdownColor: "#15803d",
  });
  const [errorData, setErrorData] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  const { title, image, imageName, oldImage, link, message, backgroundColor, messageSpeed, displayLocation, active, expiresAt, countdownColor } =
    formData;

  const { notification, loading: detailLoading } = useSelector(
    (state) => state.imageNotificationDetail
  );

  const { isUpdated, loading: listLoading, error, errors } = useSelector(
    (state) => state.imageNotificationList
  );

  useEffect(() => {
    dispatch(getImageNotificationDetail(notificationId));
  }, [dispatch, notificationId]);

  useEffect(() => {
    if (notification && notification._id === notificationId && !isLoaded) {
      setFormData({
        title: notification.title || "",
        image: "",
        imageName: "",
        oldImage: notification.image || "",
        link: notification.link || "",
        message: notification.message || "",
        backgroundColor: notification.backgroundColor || "#16a34a",
        messageSpeed: notification.messageSpeed || 5,
        displayLocation: notification.displayLocation || "landing",
        active: notification.active !== undefined ? notification.active : true,
        expiresAt: notification.expiresAt ? new Date(notification.expiresAt).toISOString().slice(0, 16) : "",
        countdownColor: notification.countdownColor || "#15803d",
      });
      if (notification.image) {
        setImagePreview(imageNotificationUrl(notification.image));
      }
      setIsLoaded(true);
    }
  }, [notification, notificationId, isLoaded]);

  useEffect(() => {
    if (errors) {
      setErrorData(errors);
    }

    if (error) {
      clearErrors();
      return alert.error(error);
    }

    if (isUpdated === true) {
      alert.success("Image notification updated successfully.");
      history.push("/admin/image-notifications");
      dispatch({ type: UPDATE_IMAGE_NOTIFICATION_RESET });
    }
  }, [dispatch, isUpdated, alert, error, errors, history]);

  const onChange = (e) => {
    if (e.target.name === "image") {
      const file = e.target.files[0];

      if (!file) return;

      if (!file.name.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
        setFormData({ ...formData, image: "", imageName: "" });
        alert.error("Please select a valid image file.");
        return;
      }

      const reader = new FileReader();
      reader.onload = function (readerEvt) {
        const binaryString = readerEvt.target.result;
        const base64String = btoa(binaryString);
        setFormData({
          ...formData,
          image: base64String,
          imageName: file.name,
        });
        setImagePreview("data:image/png;base64," + base64String);
      };
      reader.readAsBinaryString(file);
    } else if (e.target.name === "active") {
      setFormData({ ...formData, active: !active });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(
      updateImageNotification(
        notificationId,
        title,
        oldImage,
        image,
        imageName,
        link,
        message,
        backgroundColor,
        messageSpeed,
        displayLocation,
        active,
        expiresAt,
        countdownColor
      )
    );
  };

  return (
    <Fragment>
      {detailLoading || listLoading ? (
        <Loader />
      ) : (
        <Fragment>
          <div className="content-wrapper">
            <section className="content-header">
              <h1>
                Edit
                <small> Image Notification</small>
              </h1>
              <ol className="breadcrumb">
                <li>
                  <Link to="/admin">
                    <i className="fa fa-dashboard"></i> Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/admin/image-notifications">
                    <i className="fa fa-bell"></i> Image Notifications
                  </Link>
                </li>
                <li className="active">Edit</li>
              </ol>
            </section>

            <section className="content">
              <div className="box box-default">
                <div className="box-header with-border">
                  <h3 className="box-title">Edit Image Notification</h3>
                </div>

                <form onSubmit={submitHandler} encType="multipart/form-data">
                  <div className="box-body">
                    <div className="form-group row">
                      <div className="col-md-12">
                        <label htmlFor="title">Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          id="title"
                          placeholder="Enter notification title"
                          value={title}
                          onChange={onChange}
                        />
                        {errorData.title && (
                          <span className="text-danger">{errorData.title}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-12">
                        <label htmlFor="image">Image</label>
                        <input
                          type="file"
                          name="image"
                          className="form-control"
                          id="image"
                          accept=".jpg,.jpeg,.png,.gif,.webp"
                          onChange={onChange}
                        />
                        {errorData.image && (
                          <span className="text-danger">{errorData.image}</span>
                        )}
                        {imagePreview && (
                          <div style={{ marginTop: "10px" }}>
                            <p className="text-muted" style={{ fontSize: "12px" }}>
                              {imageName
                                ? "New image preview:"
                                : "Current image:"}
                            </p>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                maxWidth: "300px",
                                maxHeight: "200px",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                              }}
                            />
                            <div style={{ marginTop: "6px" }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => {
                                  setFormData({ ...formData, image: "", imageName: "", oldImage: "" });
                                  setImagePreview("");
                                  const fileInput = document.getElementById("image");
                                  if (fileInput) fileInput.value = "";
                                }}
                              >
                                <i className="fa fa-trash"></i> Remove Image
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-12">
                        <label htmlFor="link">Link (optional)</label>
                        <input
                          type="text"
                          name="link"
                          className="form-control"
                          id="link"
                          placeholder="Enter URL (e.g., https://example.com)"
                          value={link}
                          onChange={onChange}
                        />
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-12">
                        <label htmlFor="message">Scrolling Text / Marquee (optional)</label>
                        <input
                          type="text"
                          name="message"
                          className="form-control"
                          id="message"
                          placeholder="Enter scrolling banner text (e.g., Register now for 50% off!)"
                          value={message}
                          onChange={onChange}
                        />
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                          This text will scroll across a banner below the header on the landing page.
                        </p>
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-6">
                        <label htmlFor="backgroundColor">Banner Background Color</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <input
                            type="color"
                            name="backgroundColor"
                            id="backgroundColor"
                            value={backgroundColor}
                            onChange={onChange}
                            style={{ width: "50px", height: "36px", padding: "2px", cursor: "pointer" }}
                          />
                          <input
                            type="text"
                            name="backgroundColor"
                            className="form-control"
                            value={backgroundColor}
                            onChange={onChange}
                            style={{ maxWidth: "120px" }}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="messageSpeed">Scroll Speed (1-10)</label>
                        <input
                          type="number"
                          name="messageSpeed"
                          className="form-control"
                          id="messageSpeed"
                          min="1"
                          max="10"
                          value={messageSpeed}
                          onChange={onChange}
                        />
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                          Higher = faster scroll speed
                        </p>
                      </div>
                    </div>

                    {message && (
                      <div className="form-group row">
                        <div className="col-md-12">
                          <label>Banner Preview</label>
                          <div
                            style={{
                              backgroundColor: backgroundColor,
                              color: "#fff",
                              padding: "8px 16px",
                              borderRadius: "4px",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-block",
                                animation: `marquee ${200 / messageSpeed}s linear infinite`,
                                fontWeight: 600,
                              }}
                            >
                              {message}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-group row">
                      <div className="col-md-12">
                        <label htmlFor="countdownColor">Countdown Timer Color</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                          <input
                            type="color"
                            name="countdownColor"
                            id="countdownColor"
                            value={countdownColor}
                            onChange={onChange}
                            style={{ width: "50px", height: "36px", padding: "2px", cursor: "pointer" }}
                          />
                          <input
                            type="text"
                            name="countdownColor"
                            className="form-control"
                            value={countdownColor}
                            onChange={onChange}
                            style={{ maxWidth: "120px" }}
                          />
                        </div>
                        <label htmlFor="expiresAt">Countdown Expiry Date (optional)</label>
                        <div>
                          <DatePicker
                            selected={expiresAt ? new Date(expiresAt) : null}
                            onChange={(date) => setFormData({ ...formData, expiresAt: date ? date.toISOString() : "" })}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="form-control"
                            placeholderText="Select expiry date and time"
                            isClearable
                            minDate={new Date()}
                          />
                        </div>
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                          If set, a countdown timer will display on the popup showing time remaining until this date.
                        </p>
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-6">
                        <label htmlFor="displayLocation">Display Location</label>
                        <select
                          name="displayLocation"
                          className="form-control"
                          id="displayLocation"
                          value={displayLocation}
                          onChange={onChange}
                        >
                          <option value="landing">Landing Page</option>
                          <option value="admin">Admin</option>
                          <option value="both">Both</option>
                        </select>
                        {errorData.displayLocation && (
                          <span className="text-danger">
                            {errorData.displayLocation}
                          </span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label>Status</label>
                        <div className="checkbox">
                          <label>
                            <input
                              type="checkbox"
                              name="active"
                              checked={active}
                              onChange={onChange}
                            />{" "}
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="box-footer">
                    <button type="submit" className="btn btn-primary">
                      <i className="fa fa-save"></i> Update
                    </button>
                    <Link
                      to="/admin/image-notifications"
                      className="btn btn-default"
                      style={{ marginLeft: "10px" }}
                    >
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </section>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default EditImageNotification;
